// ============================================================
// rebody - changing WHAT KIND of thing a live combatant is, and how many.
//
// The sheet's half of `rebodied` (engine/targetEdits): the stored body
// type, the crowd flag and the new body's default armour go on the
// SHEET, and the live state loses what the new shape cannot carry. Pure.
// ============================================================
import type { Character } from '../character/types';
import { defaultArmourForBody } from '../bodyConfig';
import { bodyTypeFor, type BodyShape } from '../bodyShape';
import { keepsCondition } from '../targetEdits';
import type { CombatState } from './types';

/** A crowd nobody has sized yet starts at this magnitude. */
const DEFAULT_MAGNITUDE = 30;

/** The sheet's partial for a new shape and crowd-ness: body type, horde flag, the body's default armour. */
export function rebodyPatch(sheet: Character, shape: BodyShape, crowd: boolean): Partial<Character> {
  const bodyType = bodyTypeFor(shape, crowd);
  const horde = {
    ...sheet.horde,
    isHorde: crowd,
    maxMagnitude: sheet.horde?.maxMagnitude || DEFAULT_MAGNITUDE,
  };
  return { bodyType, horde, armour: { ...sheet.armour, locations: defaultArmourForBody(bodyType) } };
}

/**
 * The live state in the new body: its armour (the body's default unless
 * `armour` says otherwise - a body switch resets armour, worn or not), a vehicle's facing back to Auto, the
 * conditions the new shape cannot carry removed, and a crowd that had
 * no magnitude given one.
 */
export function rebodiedState(
  state: CombatState,
  shape: BodyShape,
  crowd: boolean,
  armour: CombatState['armour'] = defaultArmourForBody(bodyTypeFor(shape, crowd)),
): CombatState {
  return {
    ...state,
    armour: { ...armour },
    vehicleFacing: shape === 'vehicle' ? 'auto' : null,
    conditions: (state.conditions ?? []).filter((code) => keepsCondition(code, shape)),
    hordeMagnitude: crowd ? state.hordeMagnitude || DEFAULT_MAGNITUDE : state.hordeMagnitude,
  };
}
